import {
  DataListTagInfo,
  DataPagination,
  DataTagsSummary,
  RequestParams,
} from '@robovision/data'
import {
  type DataTag,
  QueryClient,
  skipToken,
  useQuery,
} from '@tanstack/vue-query'
import { AxiosResponse } from 'axios'
import {
  computed,
  MaybeRefOrGetter,
  toValue,
} from 'vue'
import { Tag } from '~domain/Tag'
import {
  dataApi,
} from '~services/api'
import { queryClient } from '~services/query-client'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'


type ListFilteredPoolTagsQuery = Parameters<typeof dataApi.pools.listFilteredPoolTags>[1]

type ListFilteredPoolTagsParams = Omit<RequestParams, 'signal' | 'validateStatus'>
  & { signal?: AbortSignal }

const QUERY_KEY_PREFIX = ['dataApi', 'pools', 'listFilteredPoolTags']

function listFilteredPoolTagsQueryKey (poolId: string, query: DataListTagInfo) {
  const {
    pagination,
    ...filters
  } = query

  return [
    ...QUERY_KEY_PREFIX,
    poolId,
    filters,
    pagination,
  ] as unknown as DataTag<readonly unknown[], {
    nextPagination: DataPagination
    tags: Tag[]
  }>
}

function transformResponse (response: AxiosResponse<DataTagsSummary>): {
  nextPagination: DataPagination
  tags: Tag[]
} {
  const {
    tags,
    nextPagination,
  } = response.data

  return {
    tags: tags.map(Tag.fromResponse),
    nextPagination,
  }
}

export const ALL_TAGS_WITHOUT_SAMPLE_COUNT: ListFilteredPoolTagsQuery = {
  includeSampleCount: false,
  pagination: {
    limit: null,
    offset: 0,
  },
  sampleFilters: [],
}

function getListFilteredPoolTagsQueryFn (
  datapoolId: string,
  query: ListFilteredPoolTagsQuery,
  params?: ListFilteredPoolTagsParams,
) {
  return ({ signal }: { signal: AbortSignal }) => dataApi.pools
    .listFilteredPoolTags(datapoolId, query, {
      ...RETHROW_ERROR_AFTER_HANDLING,
      ...params,
      signal: params?.signal
        ? AbortSignal.any([signal, params.signal])
        : signal,
    })
    .then(transformResponse)
}

export function useListFilteredPoolTagsQuery (
  {
    datapoolId,
    params,
    query,
  }: {
    datapoolId: MaybeRefOrGetter<string | undefined | null>
    params?: MaybeRefOrGetter<ListFilteredPoolTagsParams>
    query: MaybeRefOrGetter<ListFilteredPoolTagsQuery>
  },
  queryClient?: QueryClient,
) {
  return useQuery(
    {
      queryKey: computed(() => {
        const poolIdValue = toValue(datapoolId)
        const queryValue = toValue(query)

        return poolIdValue
          ? listFilteredPoolTagsQueryKey(poolIdValue, queryValue)
          : [...QUERY_KEY_PREFIX, 'absent pool id']
      }),
      queryFn: computed(() => {
        const datapoolIdValue = toValue(datapoolId)

        if (!datapoolIdValue) return skipToken

        return getListFilteredPoolTagsQueryFn(
          datapoolIdValue,
          toValue(query),
          toValue(params),
        )
      }),
    },
    queryClient,
  )
}

export function listFilteredPoolTags (
  datapoolId: string,
  query: ListFilteredPoolTagsQuery,
  params?: ListFilteredPoolTagsParams,
) {
  return queryClient.fetchQuery({
    queryFn: getListFilteredPoolTagsQueryFn(datapoolId, query, params),
    queryKey: listFilteredPoolTagsQueryKey(
      datapoolId,
      query,
    ),
  })
}
